import { Info } from 'lucide-react';
import type { ReactNode } from 'react';

import { Card, CardContent } from '~/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

export type KpiTone = 'default' | 'success' | 'warning' | 'error';

interface Props {
  label: string;
  value: string;
  /** Tooltip content shown when hovering the info icon next to the label. */
  info?: string;
  /** Inline hint shown under the label. */
  hint?: string;
  /** Colors the value text. */
  tone?: KpiTone;
  /** Merged into the outer Card. */
  className?: string;
  /** Rendered after the label/info icon, e.g. a revenue source tag. */
  labelAdornment?: ReactNode;
}

const toneStyles: Record<KpiTone, string> = {
  default: 'text-foreground',
  success: 'text-success',
  warning: 'text-warning',
  error: 'text-error',
};

export function ExperimentKpiCard({
  label,
  value,
  info,
  hint,
  tone = 'default',
  className,
  labelAdornment,
}: Props) {
  return (
    <Card className={cn(className, 'border-0 shadow-xs')}>
      <CardContent className="p-4">
        <p className={cn('text-lg font-semibold tracking-tight', toneStyles[tone])}>{value}</p>
        <div className="text-muted-foreground mt-1 flex items-center gap-1 text-sm">
          <span>{label}</span>
          {info && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground inline-flex cursor-help"
                  aria-label={`${label} info`}
                >
                  <Info className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                {info}
              </TooltipContent>
            </Tooltip>
          )}
          {labelAdornment}
        </div>
        {hint && <p className="text-muted-foreground mt-1 text-xs">{hint}</p>}
      </CardContent>
    </Card>
  );
}
